import React, { useState } from 'react';
import { Head } from 'vite-react-ssg';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import CustomCursor from '../components/CustomCursor';
import DontBeEvilSection from '@/components/DontBeEvilSection';
import EntrepreneurshipSection from '@/components/EntrepreneurshipSection';
import MarqueeSection from '@/components/MarqueeSection';
import FAQ from '@/components/FAQ';
import GreenPlanetSection from '@/components/GreenPlanetSection';

const AboutUs = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const silk = [0.25, 0.46, 0.45, 0.94];
  const expo = [0.16, 1, 0.3, 1];

  const eyebrowV = { hidden:{opacity:0,y:14}, visible:{opacity:1,y:0,transition:{duration:0.7,ease:silk}} };
  const wordV    = { hidden:{opacity:0,y:44,rotateX:14}, visible:{opacity:1,y:0,rotateX:0,transition:{duration:0.8,ease:expo}} };
  const italicV  = { hidden:{opacity:0,x:-36,skewX:-5}, visible:{opacity:1,x:0,skewX:0,transition:{duration:0.7,ease:expo}} };

  return (
    <div className={darkMode ? 'dark' : ''}>

<Head>
  {/* ── Basic Meta ── */}
  <title>About VibrantViva | Direct Selling, Entrepreneurship and Growth</title>
  <meta
    name="description"
    content="Learn about VibrantViva's mission, vision, and commitment to empowering entrepreneurs through direct selling, leadership development, business growth, and long-term success."
  />
  <meta
    name="keywords"
    content="Direct Selling Company India, Legal Direct Selling, Network Marketing India, MLM Business Opportunity, Entrepreneur Opportunity, Business Opportunity India, Entrepreneurship Development, Entrepreneur Success Platform, Leadership Development, Professional Growth, Success Opportunity, business opportunity for entrepreneurs, long term growth opportunity, VibrantViva Business Opportunity, Entrepreneurship, Entrepreneur Network"
  />
  <meta name="author" content="VibrantViva" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://vibrantviva.com/about-us" />

  {/* ── Open Graph ── */}
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://vibrantviva.com/about-us" />
  <meta
    property="og:title"
    content="About VibrantViva | Direct Selling, Entrepreneurship & Growth"
  />
  <meta
    property="og:description"
    content="Learn about VibrantViva's mission, vision, and commitment to empowering entrepreneurs through direct selling, leadership development, business growth, and long-term success."
  />
  <meta property="og:image" content="https://vibrantviva.com/assets/logo.png" />
  <meta property="og:image:width" content="200" />
  <meta property="og:image:height" content="200" />
  <meta property="og:site_name" content="VibrantViva" />
  <meta property="og:locale" content="en_IN" />

  {/* ── JSON-LD Structured Data ── */}
  <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "url": "https://vibrantviva.com/about-us",
      "name": "About VibrantViva",
      "description":
        "Learn about VibrantViva's mission, vision, and commitment to empowering entrepreneurs through direct selling, leadership development, business growth, and long-term success.",
      "inLanguage": "en-IN",
      "isPartOf": {
        "@type": "WebSite",
        "name": "VibrantViva",
        "url": "https://vibrantviva.com/"
      },
      "publisher": {
        "@type": "Organization",
        "name": "VibrantViva",
        "url": "https://vibrantviva.com/",
        "logo": "https://vibrantviva.com/assets/logo.png",
        "sameAs": [
          "https://www.facebook.com/media/set/?vanity=officialVibrantViva&set=a.705168821617957",
          "https://www.instagram.com/vibrantvivaofficial/",
          "https://www.linkedin.com/company/indusviva-healthsciences-private-limited/posts/?feedView=all&viewAsMember=true",
          "https://x.com/IndusvivaPvt?t=yf_P18W0HW6DR9ZbrhjRzg&s=09",
          "https://www.youtube.com/@VibrantViva"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-1800-103-4916",
          "contactType": "Customer Support",
          "hoursAvailable": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday"
              ],
              "opens": "09:30",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Saturday"],
              "opens": "09:30",
              "closes": "13:30"
            }
          ],
          "availableLanguage": [
            "English",
            "Hindi",
            "Kannada",
            "Tamil",
            "Telugu",
            "Malayalam"
          ],
          "areaServed": "IN",
          "contactOption": "TollFree"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress":
            "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
          "addressLocality": "Bangalore Urban",
          "addressRegion": "Karnataka",
          "postalCode": "560046",
          "addressCountry": "IN"
        },
        "location": {
          "@type": "Place",
          "name": "Viva Tower",
          "address": {
            "@type": "PostalAddress",
            "streetAddress":
              "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
            "addressLocality": "Bangalore Urban",
            "addressRegion": "Karnataka",
            "postalCode": "560046",
            "addressCountry": "IN"
          }
        }
      }
    })}
  }
  />
</Head>

      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .about-hero { background: #0F0A07; padding-bottom: 64px; }
        .about-eyebrow {
          font-family: 'Outfit', sans-serif; font-size: 0.6rem; font-weight: 500;
          letter-spacing: 0.3em; text-transform: uppercase; color: #B8926A;
          display: flex; align-items: center; gap: 12px;
        }
        .about-eyebrow-line { display: inline-block; width: 32px; height: 1px; background: #6B4226; }
        .about-heading {
          font-family: 'Cormorant Garamond', serif; font-weight: 500;
          letter-spacing: -0.015em; line-height: 1.15; color: #F5ECD8; perspective: 700px;
        }
        .about-heading-italic {
          font-family: 'Cormorant Garamond', serif; font-style: italic;
          font-weight: 400; color: #C9956A; display: block; margin-top: 4px;
        }
        .about-desc {
          font-family: 'Outfit', sans-serif; font-size: 0.82rem; font-weight: 300;
          color: rgba(245,236,216,0.7); line-height: 1.75; max-width: 380px;
        }
        .about-divider {
          height: 1px;
          background: linear-gradient(to right, rgba(201,149,106,0.4), transparent);
          margin-top: 20px;
        }
      `}} />

      <CustomCursor />

      {/* ── Hero banner ── */}
      <div className="about-hero">
        <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        <div ref={ref} className="container mx-auto px-6 max-w-6xl pt-28 pb-2">

          <motion.p
            className="about-eyebrow mb-5 mt-10"
            variants={eyebrowV} initial="hidden" animate={inView ? 'visible' : 'hidden'}
          >
            <span className="about-eyebrow-line" />
            Our Story
          </motion.p>

          <div className="flex items-end gap-10">
            <motion.h1
              className="about-heading text-4xl md:text-5xl"
              variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.12, delayChildren:0.18 } } }}
              initial="hidden" animate={inView ? 'visible' : 'hidden'}
            >
              <motion.span variants={wordV} style={{ display:'inline-block' }}>About </motion.span>
              <motion.span className="about-heading-italic" variants={italicV} style={{ display:'inline-block' }}> Us</motion.span>
            </motion.h1>

            <motion.div
              style={{ width:1, alignSelf:'stretch', background:'linear-gradient(to bottom, transparent, rgba(201,149,106,0.4), transparent)', marginBottom:6 }}
              className="hidden md:block"
              initial={{ opacity:0, scaleY:0 }}
              animate={inView ? { opacity:1, scaleY:1 } : {}}
              transition={{ duration:0.7, ease:expo, delay:0.3 }}
            />

            <motion.p
              className="about-desc hidden md:block"
              variants={eyebrowV} initial="hidden" animate={inView ? 'visible' : 'hidden'}
            >
              Learn who we are, what drives us, and the values that guide every step of our journey.
            </motion.p>

            <motion.p
              className="about-desc md:hidden"
              variants={eyebrowV} initial="hidden" animate={inView ? 'visible' : 'hidden'}
            >
              Learn who we are, what drives us, and the values that guide every step of our journey.
            </motion.p>
          </div>

          <motion.div
            className="about-divider"
            initial={{ scaleX:0, opacity:0 }}
            animate={inView ? { scaleX:1, opacity:1 } : {}}
            transition={{ duration:1, ease:expo, delay:0.5 }}
            style={{ transformOrigin:'left' }}
          />
        </div>
      </div>

      {/* ── Page sections ── */}
      <DontBeEvilSection />
      <EntrepreneurshipSection />
      <GreenPlanetSection />
      <MarqueeSection />
      <FAQ />

      <Footer />
      <BackToTop />
    </div>
  );
};

export default AboutUs;